import Router, { useRouter } from 'next/router';
import { memo, type ReactNode, useCallback } from 'react';

import { getQueryWithNullAssetModalParams } from '~/components/AssetModalV2/utils';
import { QueryParamNames } from '~/constants/search';
import {
  type PublicAssetModalNavigationContextValue,
  PublicAssetModalNavigationProvider,
} from '~/providers/PublicAssetModalNavigationProvider';
import { getClipIdFromPath, pushWithExistingQuery } from '~/utils/PathUtils';

export type PublicUrlAssetModalNavigationProviderProps = {
  children: ReactNode;
};

/**
 * The URL implementation of the asset-modal navigation contract, for every surface whose URL
 * really works: the live `/a/…` pages and the editor's Preview iframe, whose preview routes carry
 * the same `/c/{clipId}` segment on their own base path. Opening the modal appends `/c/{clipId}`
 * to whatever route is showing (clearing the modal deep-link query params it supersedes), closing
 * strips it, and the active clip is read back off the path — so deep links, back/forward, and
 * refresh all behave, on both surfaces, with no preview special-casing.
 *
 * The collection link editor's Edit tab lives on the editor's own route instead, so it supplies
 * state-backed bindings (see ShowcaseEditorAssetModalNavigationProvider) rather than
 * mounting this.
 */
export const PublicUrlAssetModalNavigationProvider = memo(
  ({ children }: PublicUrlAssetModalNavigationProviderProps) => {
    const { asPath } = useRouter();
    const activeClipId = getClipIdFromPath(asPath) ?? null;

    const onOpenAssetModal = useCallback<PublicAssetModalNavigationContextValue['onOpenAssetModal']>(
      ({ clipId, query }) => {
        Router.ready(() => {
          /**
           * Split away any query params
           * Split away a previous clip id that might be there
           */
          const currentBasePath = Router.asPath.split('?')[0].split('/c/')[0];

          pushWithExistingQuery({
            path: `${currentBasePath}/c/${clipId}`,
            newQuery: {
              [QueryParamNames.notificationsPanel]: null,
              [QueryParamNames.discussionId]: null,
              [QueryParamNames.page]: null,
              [QueryParamNames.faceId]: null,
              ...query,
            },
          });
        });
      },
      [],
    );

    const onCloseAssetModal = useCallback(() => {
      Router.ready(() => {
        pushWithExistingQuery({
          path: Router.asPath.split('/c/')[0],
          newQuery: getQueryWithNullAssetModalParams(),
        });
      });
    }, []);

    return (
      <PublicAssetModalNavigationProvider
        activeClipId={activeClipId}
        onCloseAssetModal={onCloseAssetModal}
        onOpenAssetModal={onOpenAssetModal}
      >
        {children}
      </PublicAssetModalNavigationProvider>
    );
  },
);

PublicUrlAssetModalNavigationProvider.displayName = 'PublicUrlAssetModalNavigationProvider';
